<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  offer: { type: Object, default: null },
  contract: { type: Object, default: null },
  providerStatus: { type: Object, default: null },
  pdfUrl: { type: String, default: '' },
  templateId: { type: String, default: '' },
  cancelReason: { type: String, default: '' },
  contractAction: { type: String, default: '' },
  resultMessage: { type: String, default: '' },
  errorMessage: { type: String, default: '' },
  canGenerate: { type: Boolean, default: false },
  canSync: { type: Boolean, default: false },
  canCancel: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'generate',
  'sync',
  'cancel',
  'open-signer',
  'update:pdfUrl',
  'update:templateId',
  'update:cancelReason',
]);

const showCancelConfirmation = ref(false);

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) showCancelConfirmation.value = false;
  }
);

watch(
  () => props.resultMessage,
  message => {
    if (message) showCancelConfirmation.value = false;
  }
);

const asArray = value => (Array.isArray(value) ? value : []);

const normalizeToken = value =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const offerReference = computed(
  () =>
    props.offer?.booking_reference ||
    props.offer?.sales_flow_id ||
    props.offer?.id ||
    'sem referência'
);

const statusLabel = status =>
  ({
    pending: 'Aguardando geração',
    sent: 'Enviado',
    partially_signed: 'Parcialmente assinado',
    signed: 'Assinado',
    canceled: 'Cancelado',
    failed: 'Falhou',
  })[normalizeToken(status)] ||
  status ||
  'Não gerado';

const statusClass = status =>
  ({
    sent: 'bg-blue-50 text-blue-700',
    partially_signed: 'bg-amber-50 text-amber-700',
    signed: 'bg-emerald-50 text-emerald-700',
    canceled: 'bg-slate-100 text-slate-600',
    failed: 'bg-rose-50 text-rose-700',
  })[normalizeToken(status)] || 'bg-lais-bg-soft text-lais-text-muted';

const providerStatusLabel = status =>
  ({
    healthy: 'Provider conectado',
    unhealthy: 'Provider com pendência',
    unavailable: 'Provider indisponível',
  })[normalizeToken(status)] ||
  status ||
  'Provider não verificado';

const providerName = computed(
  () => props.contract?.provider || props.providerStatus?.provider || 'zapsign'
);

const providerAlert = computed(() => {
  if (props.contract?.status === 'failed') {
    return (
      props.contract.last_error ||
      'O provider de assinatura retornou uma falha. Sincronize novamente depois de revisar a integração.'
    );
  }

  const status = normalizeToken(props.providerStatus?.status);
  if (
    props.providerStatus &&
    (props.providerStatus.active === false ||
      ['unhealthy', 'unavailable'].includes(status))
  ) {
    return (
      props.providerStatus.message ||
      'A integração de assinatura digital precisa ser revisada antes de gerar ou sincronizar o contrato.'
    );
  }

  return '';
});

const documentLabel = computed(() =>
  props.contract?.external_doc_token
    ? 'Documento ZapSign vinculado'
    : 'Sem documento vinculado'
);

const activityRows = computed(() =>
  [
    { label: 'Provider', value: providerName.value },
    {
      label: 'Status provider',
      value: providerStatusLabel(props.providerStatus?.status),
    },
    {
      label: 'Última sync',
      value: formatDate(props.contract?.last_synced_at),
    },
    { label: 'Enviado em', value: formatDate(props.contract?.sent_at) },
    { label: 'Assinado em', value: formatDate(props.contract?.signed_at) },
    { label: 'Cancelado em', value: formatDate(props.contract?.canceled_at) },
  ].filter(row => row.value && row.value !== '—')
);

const signerRoleLabel = role =>
  ({
    owner: 'Proprietário',
    landlord: 'Proprietário',
    seller: 'Proprietário',
    supply: 'Proprietário',
    tenant: 'Interessado',
    buyer: 'Interessado',
    renter: 'Interessado',
    demand: 'Interessado',
    agent: 'Corretor',
    broker: 'Corretor',
  })[normalizeToken(role)] ||
  role ||
  'Signatário';

const signerStatusLabel = status =>
  ({
    signed: 'Assinatura concluída',
    completed: 'Assinatura concluída',
    new: 'Pendente de assinatura',
    pending: 'Pendente de assinatura',
    email_sent: 'Pendente de assinatura',
    waiting: 'Pendente de assinatura',
    refused: 'Assinatura recusada',
    rejected: 'Assinatura recusada',
  })[normalizeToken(status)] ||
  status ||
  'Pendente de assinatura';

const signerStatusClass = status =>
  ({
    signed: 'bg-emerald-50 text-emerald-700',
    completed: 'bg-emerald-50 text-emerald-700',
    refused: 'bg-rose-50 text-rose-700',
    rejected: 'bg-rose-50 text-rose-700',
  })[normalizeToken(status)] || 'bg-amber-50 text-amber-700';

const signerRows = computed(() =>
  asArray(props.contract?.signers).map((signer, index) => ({
    key: signer.token || signer.external_id || `${signer.role}-${index}`,
    role: signer.role || signer.qualification,
    label: signerRoleLabel(signer.role || signer.qualification),
    statusLabel: signerStatusLabel(signer.status),
    statusClass: signerStatusClass(signer.status),
    signedAt: formatDate(signer.signed_at),
    signUrl: signer.sign_url,
  }))
);

const attemptRows = computed(() =>
  asArray(
    props.contract?.webhook_attempts ||
      props.contract?.provider_attempts ||
      props.contract?.attempts
  ).map((attempt, index) => ({
    key: attempt.id || attempt.provider_event_id || `attempt-${index}`,
    title: attempt.event_type || 'Tentativa provider',
    status: attempt.outcome || attempt.remote_status || 'unknown',
    detail: attempt.failure_code || attempt.remote_status || attempt.provider,
    receivedAt: formatDate(
      attempt.received_at || attempt.created_at || attempt.remote_event_at
    ),
  }))
);

const eventRows = computed(() =>
  asArray(
    props.contract?.webhook_events ||
      props.contract?.provider_events ||
      props.contract?.events
  ).map((event, index) => ({
    key: event.id || event.provider_event_id || `event-${index}`,
    title: event.event_type || 'Evento provider',
    status: event.remote_status || event.status || event.provider,
    receivedAt: formatDate(
      event.received_at || event.created_at || event.remote_event_at
    ),
  }))
);

const cancelReasonReady = computed(() => props.cancelReason.trim().length >= 5);
const isBusy = computed(() => Boolean(props.contractAction));

const confirmCancel = () => {
  if (!cancelReasonReady.value || isBusy.value) return;
  emit('cancel');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisDrawer
    :show="show"
    title="Contrato da oferta"
    side="right"
    size="lg"
    close-label="Fechar contrato"
    @close="emit('close')"
  >
    <template #header>
      <div class="min-w-0">
        <p class="text-[12px] font-semibold text-lais-text-muted">
          Proposta {{ offerReference }}
        </p>
        <div class="mt-1 flex flex-wrap items-center gap-2">
          <h2
            class="font-redHatDisplay text-[20px] font-semibold text-lais-text"
          >
            Contrato da oferta
          </h2>
          <span
            class="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
            :class="statusClass(contract?.status)"
          >
            {{ statusLabel(contract?.status) }}
          </span>
        </div>
      </div>
    </template>

    <div class="grid gap-4">
      <div
        v-if="resultMessage"
        role="status"
        class="rounded-lais-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] leading-relaxed text-emerald-700"
      >
        {{ resultMessage }}
      </div>

      <div
        v-if="errorMessage"
        role="alert"
        class="rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-700"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="providerAlert"
        role="alert"
        class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800"
      >
        <p class="font-semibold">Provider precisa de atenção</p>
        <p class="mt-1">{{ providerAlert }}</p>
      </div>

      <section
        class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="text-[12px] font-semibold text-lais-text-muted">
              Provider
            </p>
            <p class="mt-0.5 text-[15px] font-semibold text-lais-text">
              {{ providerName }}
            </p>
          </div>
          <span
            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-lais-primary"
          >
            {{ providerStatusLabel(providerStatus?.status) }}
          </span>
        </div>

        <dl class="grid gap-2 text-[13px]">
          <div
            v-for="row in activityRows"
            :key="row.label"
            class="flex justify-between gap-3"
          >
            <dt class="text-lais-text-muted">{{ row.label }}</dt>
            <dd class="text-right font-semibold text-lais-text">
              {{ row.value }}
            </dd>
          </div>
        </dl>

        <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-[13px]">
          <div class="flex justify-between gap-3">
            <span class="text-lais-text-muted">Documento</span>
            <span class="text-right font-semibold text-lais-text">
              {{ documentLabel }}
            </span>
          </div>
          <a
            v-if="contract?.signed_file_url"
            :href="contract.signed_file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-2 inline-flex items-center gap-1 font-semibold text-lais-link"
          >
            <span class="i-lucide-file-check-2 size-4" aria-hidden="true" />
            Arquivo assinado
          </a>
        </div>
      </section>

      <section
        class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
      >
        <h3 class="text-[14px] font-semibold text-lais-text">Signatários</h3>
        <div v-if="signerRows.length" class="grid gap-2">
          <div
            v-for="signer in signerRows"
            :key="signer.key"
            class="grid gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <p class="font-semibold text-lais-text">{{ signer.label }}</p>
                <span
                  class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  :class="signer.statusClass"
                >
                  {{ signer.statusLabel }}
                </span>
              </div>
              <p
                v-if="signer.signedAt !== '—'"
                class="mt-1 text-[12px] text-lais-text-muted"
              >
                Assinado em {{ signer.signedAt }}
              </p>
            </div>
            <a
              v-if="signer.signUrl"
              :href="signer.signUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-full border border-lais-border px-3 text-[12px] font-semibold text-lais-link transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
              @click="emit('open-signer', signer)"
            >
              <span
                class="i-lucide-external-link size-3.5"
                aria-hidden="true"
              />
              Abrir assinatura
            </a>
          </div>
        </div>
        <p v-else class="text-[13px] text-lais-text-muted">
          Nenhum signatário retornado pelo provider.
        </p>
      </section>

      <section
        class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
      >
        <h3 class="text-[14px] font-semibold text-lais-text">
          Tentativas do provider
        </h3>
        <div v-if="attemptRows.length" class="grid gap-2">
          <div
            v-for="attempt in attemptRows"
            :key="attempt.key"
            class="rounded-lais-sm border border-lais-border-soft px-3 py-2 text-[13px]"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="font-semibold text-lais-text">{{ attempt.title }}</p>
              <span class="text-[12px] text-lais-text-muted">
                {{ attempt.receivedAt }}
              </span>
            </div>
            <p class="mt-1 text-lais-text-body">
              {{ attempt.status }}
              <template v-if="attempt.detail">
                · {{ attempt.detail }}
              </template>
            </p>
          </div>
        </div>
        <p v-else class="text-[13px] text-lais-text-muted">
          Nenhuma tentativa registrada para este contrato.
        </p>
      </section>

      <section
        class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
      >
        <h3 class="text-[14px] font-semibold text-lais-text">
          Eventos recebidos
        </h3>
        <div v-if="eventRows.length" class="grid gap-2">
          <div
            v-for="event in eventRows"
            :key="event.key"
            class="rounded-lais-sm border border-lais-border-soft px-3 py-2 text-[13px]"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="font-semibold text-lais-text">{{ event.title }}</p>
              <span class="text-[12px] text-lais-text-muted">
                {{ event.receivedAt }}
              </span>
            </div>
            <p class="mt-1 text-lais-text-body">{{ event.status }}</p>
          </div>
        </div>
        <p v-else class="text-[13px] text-lais-text-muted">
          Nenhum evento recebido para este contrato.
        </p>
      </section>

      <section
        v-if="canGenerate"
        class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
      >
        <h3 class="text-[14px] font-semibold text-lais-text">Geração</h3>
        <div class="grid gap-3 md:grid-cols-2">
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            PDF do contrato
            <input
              :value="pdfUrl"
              type="url"
              placeholder="https://..."
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              @input="emit('update:pdfUrl', $event.target.value)"
            />
          </label>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Template provider
            <input
              :value="templateId"
              type="text"
              placeholder="template_id"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              @input="emit('update:templateId', $event.target.value)"
            />
          </label>
        </div>
      </section>

      <section
        v-if="showCancelConfirmation"
        class="grid gap-3 rounded-lais border border-rose-200 bg-rose-50 p-4"
      >
        <div>
          <h3 class="text-[14px] font-semibold text-rose-800">
            Confirmar cancelamento
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed text-rose-700">
            O contrato será cancelado no provider e o motivo ficará vinculado à
            operação local.
          </p>
        </div>
        <label class="grid gap-1 text-[12px] font-semibold text-rose-800">
          Motivo do cancelamento
          <textarea
            :value="cancelReason"
            rows="3"
            placeholder="Ex.: contrato duplicado, erro de dados, solicitação do cliente"
            class="rounded-lais border border-rose-200 bg-white px-3 py-2 text-[14px] font-normal text-lais-text placeholder:text-lais-text-muted focus:border-rose-400 focus:outline-none"
            @input="emit('update:cancelReason', $event.target.value)"
          />
        </label>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            :disabled="isBusy"
            @click="showCancelConfirmation = false"
          >
            Voltar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            size="sm"
            icon="i-lucide-ban"
            :disabled="!cancelReasonReady"
            :is-loading="contractAction === 'cancel'"
            @click="confirmCancel"
          >
            Confirmar cancelamento
          </LaisPrimaryButton>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <LaisPrimaryButton
          v-if="canGenerate"
          size="sm"
          icon="i-lucide-send"
          :is-loading="contractAction === 'generate'"
          :disabled="isBusy && contractAction !== 'generate'"
          @click="emit('generate')"
        >
          Gerar contrato
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canSync"
          size="sm"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="contractAction === 'sync'"
          :disabled="isBusy && contractAction !== 'sync'"
          @click="emit('sync')"
        >
          Atualizar status
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canCancel && !showCancelConfirmation"
          size="sm"
          variant="ghost"
          icon="i-lucide-ban"
          :disabled="isBusy"
          @click="showCancelConfirmation = true"
        >
          Cancelar contrato
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>
</template>
